/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


/*
 * The one filter toolbar every list page uses: page-specific filters first, then the search box that
 * eats the rest of the row, then the search / clear buttons. Keeping the markup here is what makes the
 * pages look identical — a page that hand-rolls the row always ends up a little off.
 *
 * Note the search Input carries no width class: Input treats any `w-*` / `min-w-[…]` class as "the page
 * sets my width" and drops its own `w-full`, which is exactly how the box used to collapse to ~200px.
 * The width lives on the wrapper below instead.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Card, CardContent } from './Card';
import { Button } from './Button';
import { Input } from './Input';

export interface FilterBarProps {
  /** Page-specific filters (status dropdowns, date pickers, toggles) rendered before the search box. */
  children?: React.ReactNode;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  /** Omit to filter as the user types — the magnifier button is then hidden. */
  onSearch?: () => void;
  onClearSearch: () => void;
  searchPlaceholder: string;
  /** Render bare, without the surrounding Card — for pages whose filters live inside another card. */
  card?: boolean;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  children,
  searchQuery,
  onSearchChange,
  onSearch,
  onClearSearch,
  searchPlaceholder,
  card = true,
}) => {
  const { t } = useTranslation();

  const row = (
    /* Single row on wide screens; wraps only when there is no horizontal room. */
    <div className="flex items-center gap-2 flex-wrap">
      {children}
      <div className="flex items-center gap-2 flex-1 min-w-[16rem]">
        <Input
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="flex-1"
          onKeyDown={(e) => e.key === 'Enter' && onSearch?.()}
        />
        {onSearch && (
          <Button variant="outline" size="sm" className="px-2" onClick={onSearch} title={t('common.search', 'Search')}>
            <Search className="h-4 w-4" />
          </Button>
        )}
        <Button variant="outline" size="sm" className="px-2" onClick={onClearSearch} title={t('common.clear', 'Clear')}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  if (!card) return row;

  return (
    <Card>
      <CardContent className="p-4">{row}</CardContent>
    </Card>
  );
};
